import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { T, Var, useGT } from 'gt-react';
import type { ColumnConditions } from 'handsontable/plugins/filters';
import type { PricelistTable } from '@erp/rpc';
import { orpc } from '@/api/rpc/client';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from 'src/context/ToastContext';
import { PricelistCsvGrid } from './PricelistCsvGrid';
import type { CsvCellEdit } from './pricelistCsvGridChanges';
import { PricelistCsvWindowedView } from './PricelistCsvWindowedView';
import { PricelistSearchBox } from './PricelistSearchBox';
import { PricelistSearchResults } from './PricelistSearchResults';
import { useLoadedPricelistSearch } from './usePricelistSearch';
import {
  applyEditedCells,
  buildCsvDrafts,
  csvCellKey,
  draftsToTables,
  originalCellValue,
  type CsvTableDraft,
} from './pricelistCsvEditState';
import { pricelistTableKey, pricelistTableLabel } from './pricelistTableLabel';

/** Boxed message for the view's loading/empty/error states. */
function CsvNotice({
  children,
  tone = 'muted',
}: {
  children: React.ReactNode;
  tone?: 'muted' | 'destructive';
}) {
  return (
    <div
      className={
        tone === 'destructive'
          ? 'rounded-md border px-4 py-3 text-sm text-destructive'
          : 'rounded-md border px-4 py-3 text-sm text-muted-foreground'
      }
    >
      {children}
    </div>
  );
}

/** Unsaved-edit count plus Discard/Save, shown only while there are edits. */
function UnsavedEditsBar({
  editedCellCount,
  isSaving,
  onDiscard,
  onSave,
}: {
  editedCellCount: number;
  isSaving: boolean;
  onDiscard: () => void;
  onSave: () => void;
}) {
  if (editedCellCount === 0) return null;
  return (
    <div className="flex items-center gap-2">
      <span className="text-xs text-muted-foreground">
        <T context="count of unsaved pricelist CSV cell edits">
          Unsaved edits: <Var>{editedCellCount}</Var>
        </T>
      </span>
      <Button
        variant="outline"
        size="sm"
        onClick={onDiscard}
        disabled={isSaving}
      >
        <T context="discard unsaved pricelist CSV edits">Discard</T>
      </Button>
      <Button size="sm" onClick={onSave} disabled={isSaving}>
        <T context="save edited pricelist CSV back to the pricelist">
          Save changes
        </T>
      </Button>
    </div>
  );
}

/** One tab per extracted table, shown only when there's more than one. */
function TableTabs({
  drafts,
  activeIndex,
  hitCounts,
  onChange,
}: {
  drafts: CsvTableDraft[];
  activeIndex: number;
  /** Search matches per table, when a search is running. */
  hitCounts: Record<number, number>;
  onChange: (index: number) => void;
}) {
  const gt = useGT();
  if (drafts.length < 2) return null;
  return (
    <Tabs
      value={String(activeIndex)}
      onValueChange={(value) => onChange(Number(value))}
    >
      <TabsList>
        {drafts.map((draft, index) => (
          <TabsTrigger
            key={pricelistTableKey(draft.name, draft.headers)}
            value={String(index)}
          >
            {pricelistTableLabel(gt, draft.name, index)}
            {hitCounts[index] === undefined ? null : (
              <span className="ml-1 rounded-full bg-muted px-1.5 text-[10px] tabular-nums">
                {hitCounts[index].toLocaleString()}
              </span>
            )}
          </TabsTrigger>
        ))}
      </TabsList>
    </Tabs>
  );
}

/**
 * Which table a set of column filters belongs to. Conditions name columns by
 * position, so a re-extract that changes a table's columns starts it unfiltered
 * rather than filtering the wrong ones. The document is part of it because the
 * case page swaps files under a mounted editor, and another file with the same
 * columns must not open filtered.
 */
function columnFiltersKey(
  csvDocumentId: string,
  tableIndex: number,
  draft: CsvTableDraft,
): string {
  return `${csvDocumentId}:${tableIndex}:${draft.headers.join('\0')}`;
}

interface EditorState {
  /** The fetched tables these drafts were built from. */
  source: PricelistTable[];
  drafts: CsvTableDraft[];
  editedCells: Set<string>;
  /** Bumped when the drafts are rebuilt, to reload the grids. */
  version: number;
}

function initialEditorState(tables: PricelistTable[]): EditorState {
  return {
    source: tables,
    drafts: buildCsvDrafts(tables),
    editedCells: new Set(),
    version: 0,
  };
}

/**
 * The pricelist's extracted tables as editable spreadsheets — one tab per
 * table — with the edits saved back to the stored CSV or discarded.
 */
function PricelistCsvEditor({
  csvDocumentId,
  tables,
  maxGridHeight,
}: {
  csvDocumentId: string;
  tables: PricelistTable[];
  maxGridHeight?: number;
}) {
  const gt = useGT();
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const [activeIndex, setActiveIndex] = useState(0);
  const [query, setQuery] = useState('');
  // Outside the editor state: filters are a view, so a save, discard or
  // refetch that rebuilds the drafts keeps them.
  const [columnFilters, setColumnFilters] = useState<
    Record<string, ColumnConditions[]>
  >({});

  // Rebuilt during render whenever a new fetch lands ("adjust state when props
  // change"), which drops any edits already saved to the server.
  const [state, setState] = useState<EditorState>(() =>
    initialEditorState(tables),
  );
  if (state.source !== tables) {
    setState((prev) => ({
      ...initialEditorState(tables),
      version: prev.version + 1,
    }));
  }

  const saveMutation = useMutation(
    orpc.inventoryManagement.pricelist.updateExtractedCsv.mutationOptions(),
  );

  // A re-extract can come back with fewer tables than the open tab.
  const tableIndex = Math.min(activeIndex, state.drafts.length - 1);
  const activeDraft = state.drafts[tableIndex];

  const search = useLoadedPricelistSearch({ tables: state.drafts, query });

  function handleCellsEdited(tableIndex: number, edits: CsvCellEdit[]) {
    setState((prev) => ({
      ...prev,
      editedCells: applyEditedCells(
        prev.editedCells,
        edits.map((edit) => ({
          key: csvCellKey(tableIndex, edit.rowIndex, edit.header),
          value: edit.value,
          originalValue: originalCellValue(
            prev.drafts[tableIndex],
            edit.rowIndex,
            edit.header,
          ),
        })),
      ),
    }));
  }

  function handleDiscard() {
    setState((prev) => ({
      ...prev,
      drafts: buildCsvDrafts(prev.source),
      editedCells: new Set(),
      version: prev.version + 1,
    }));
  }

  async function handleSave() {
    try {
      await saveMutation.mutateAsync({
        csvDocumentId,
        tables: draftsToTables(state.drafts),
      });
      setState((prev) => ({ ...prev, editedCells: new Set() }));
      // The save appends a version and a history event, so both sidebar lists
      // are stale too.
      const staleKeys = [
        orpc.inventoryManagement.pricelist.getExtractedCsv.key(),
        orpc.inventoryManagement.pricelist.listVersions.key(),
        orpc.inventoryManagement.pricelist.listEvents.key(),
      ];
      for (const queryKey of staleKeys) {
        void queryClient.invalidateQueries({ queryKey });
      }
      showToast({
        title: gt('Saved', {
          context: 'toast title when edited pricelist CSV is saved',
        }),
        message: gt('The pricelist CSV was updated.', {
          context: 'toast message when edited pricelist CSV is saved',
        }),
        color: 'success',
      });
    } catch {
      showToast({
        title: gt('Error', { context: 'toast title for error state' }),
        message: gt('Failed to save the pricelist CSV.', {
          context: 'toast message when saving the edited pricelist CSV fails',
        }),
        color: 'error',
      });
    }
  }

  if (!activeDraft) return null;
  const filtersKey = columnFiltersKey(csvDocumentId, tableIndex, activeDraft);

  return (
    <div className="flex flex-col gap-3">
      <PricelistSearchBox
        value={query}
        onChange={setQuery}
        isSearching={false}
      />
      <div className="flex flex-wrap items-center justify-between gap-2">
        <TableTabs
          drafts={state.drafts}
          activeIndex={tableIndex}
          hitCounts={search.hitCounts}
          onChange={setActiveIndex}
        />
        <UnsavedEditsBar
          editedCellCount={state.editedCells.size}
          isSaving={saveMutation.isPending}
          onDiscard={handleDiscard}
          onSave={() => void handleSave()}
        />
      </div>
      {search.searching ? (
        <PricelistSearchResults
          view={search}
          tableIndex={tableIndex}
          query={query}
          maxGridHeight={maxGridHeight}
        />
      ) : null}
      {/* Hidden, never unmounted or filtered, while results take its place.
          Unmounting rebuilds Handsontable over the whole table on every exit
          from search, which reads as a freeze; handing it filtered rows would be
          worse still, since it owns and mutates `draft.rows` in place and a save
          posts whole tables. */}
      <div className={search.searching ? 'hidden' : undefined}>
        <PricelistCsvGrid
          key={tableIndex}
          draft={activeDraft}
          dataVersion={state.version}
          columnFilters={{
            conditions: columnFilters[filtersKey] ?? [],
            onChange: (conditions) =>
              setColumnFilters((prev) => ({
                ...prev,
                [filtersKey]: conditions,
              })),
          }}
          onCellsEdited={(edits) => handleCellsEdited(tableIndex, edits)}
          maxHeight={maxGridHeight}
        />
      </div>
    </div>
  );
}

/**
 * The tables parsed out of one extracted pricelist CSV document, as the main
 * content of the pricelist details page.
 */
export function PricelistCsvView({
  csvDocumentId,
  maxGridHeight,
}: {
  csvDocumentId: string | undefined;
  /**
   * Caps the extracted table. The case page sets this so a long pricelist
   * scrolls inside the table and the page scroll still reaches History and
   * Discussion underneath.
   */
  maxGridHeight?: number;
}) {
  const gt = useGT();
  // Kept as the query object (not destructured) so the pending/error checks
  // below narrow `data` to a defined value.
  const csvQuery = useQuery({
    ...orpc.inventoryManagement.pricelist.getExtractedCsv.queryOptions({
      input: { documentId: csvDocumentId ?? '' },
    }),
    enabled: csvDocumentId !== undefined,
  });

  if (csvDocumentId === undefined) {
    return (
      <CsvNotice>
        <T context="empty state when a pricelist has no extracted CSV yet">
          No extraction found for this pricelist. Use “Re-extract CSV” to run
          the extraction again.
        </T>
      </CsvNotice>
    );
  }

  if (csvQuery.isPending) {
    return (
      <div className="flex flex-col gap-2 rounded-md border px-4 py-3">
        <Skeleton className="h-8 w-full" />
        <Skeleton className="h-6 w-full" />
        <Skeleton className="h-6 w-3/4" />
        <Skeleton className="h-6 w-4/5" />
      </div>
    );
  }

  if (csvQuery.isError) {
    return (
      <CsvNotice tone="destructive">
        {gt('Failed to load CSV contents.', {
          context: 'error state when extracted CSV fails to load',
        })}
      </CsvNotice>
    );
  }

  if (csvQuery.data.tables.length === 0) {
    return (
      <CsvNotice>
        {gt('No tabular data found in this CSV.', {
          context: 'empty state when extracted CSV has no table data',
        })}
      </CsvNotice>
    );
  }

  // An incomplete response means the pricelist is too large to return whole —
  // and therefore too large to edit, since saves post entire tables. It pages
  // through a read-only view instead.
  if (!csvQuery.data.complete) {
    return (
      <PricelistCsvWindowedView
        csvDocumentId={csvDocumentId}
        tables={csvQuery.data.tables}
        maxGridHeight={maxGridHeight}
      />
    );
  }

  return (
    <PricelistCsvEditor
      csvDocumentId={csvDocumentId}
      tables={csvQuery.data.tables}
      maxGridHeight={maxGridHeight}
    />
  );
}
